import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';

import {
  TAX_PROFILE_REQUIRED_ROLES,
  irs_recipient_profile,
  irs_reported_amounts,
  irs_statement,
  set_irs_recipient_profile,
} from './tax-form.model';

@Injectable({
  providedIn: 'root',
})
export class TaxFormBLL {
  //#region Loaders Observables

  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly isLoading$: Observable<boolean> = this.isLoading.asObservable();

  private isSaving: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly isSaving$: Observable<boolean> = this.isSaving.asObservable();

  private isLoading_amounts: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly isLoading_amounts$: Observable<boolean> =
    this.isLoading_amounts.asObservable();

  //#endregion Loaders Observables

  //#region Data Observables

  private profile: BehaviorSubject<irs_recipient_profile> =
    new BehaviorSubject<irs_recipient_profile>(null);
  readonly profile$: Observable<irs_recipient_profile> =
    this.profile.asObservable();

  private amounts: BehaviorSubject<irs_reported_amounts> =
    new BehaviorSubject<irs_reported_amounts>(null);
  readonly amounts$: Observable<irs_reported_amounts> =
    this.amounts.asObservable();

  /**
   * Drives the blocking onboarding step in the app shell. Starts false so the
   * step never flashes before the profile has actually been checked — a
   * false positive here locks a user out of the whole app.
   */
  private tax_profile_required: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly tax_profile_required$: Observable<boolean> =
    this.tax_profile_required.asObservable();

  //#endregion Data Observables

  /** Guards against re-checking on every auth$ emission. */
  private checked_for_user: string;

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private toast: ToastService
  ) {}

  private whenAuthenticated(): Observable<any> {
    return this.auth.auth$.pipe(
      filter((user) => !!(user && user.token)),
      take(1)
    );
  }

  /**
   * The recipient profile is always scoped to the caller's own token — no
   * user_id is sent, so one user can never read another user's tax data.
   */
  public getRecipientProfile() {
    this.isLoading.next(true);

    this.whenAuthenticated()
      .pipe(
        switchMap(() => this.api.get('tax_form?type=get_recipient_profile')),
        take(1)
      )
      .subscribe(
        (res: irs_recipient_profile) => {
          this.profile.next(res);
          this.isLoading.next(false);
        },
        (err) => {
          // A 404 just means the user has not started the form yet.
          this.profile.next(null);
          this.isLoading.next(false);
        }
      );
  }

  /**
   * Decides whether the blocking onboarding step has to be shown. Runs once per
   * signed-in user.
   *
   * Only a 404 — no profile on file — or a row that is somehow not certified
   * raises the gate. A network error or a 500 deliberately does not: the point
   * of the gate is to collect tax data, and locking someone out of the platform
   * because the API had a bad minute would be a worse failure than letting them
   * through until the next sign-in.
   */
  public checkTaxProfile(user_id: string, role: string) {
    if (!TAX_PROFILE_REQUIRED_ROLES.includes(role) || !user_id) {
      this.tax_profile_required.next(false);
      return;
    }

    if (this.checked_for_user === user_id) {
      return;
    }
    this.checked_for_user = user_id;

    this.api
      .get('tax_form?type=get_recipient_profile')
      .pipe(take(1))
      .subscribe(
        (res: irs_recipient_profile) => {
          this.profile.next(res);
          this.tax_profile_required.next(res?.status !== 'certified');
        },
        (err) => {
          this.profile.next(null);
          this.tax_profile_required.next(err?.status === 404);
        }
      );
  }

  /** Called on sign-out so the next user is re-checked rather than inheriting. */
  public resetTaxProfileCheck() {
    this.checked_for_user = null;
    this.profile.next(null);
    this.amounts.next(null);
    this.tax_profile_required.next(false);
  }

  public setRecipientProfile(set_profile: set_irs_recipient_profile) {
    this.isSaving.next(true);

    this.api
      .patch('tax_form?type=update_recipient_profile', set_profile)
      .pipe(take(1))
      .subscribe(
        (res: irs_recipient_profile) => {
          this.profile.next(res);
          // Lowering the gate here rather than in the component keeps the one
          // thing that unblocks the app tied to the one thing that satisfies
          // it: a profile the API accepted and marked certified.
          this.tax_profile_required.next(res?.status !== 'certified');
          this.toast.showToast(
            'Saved',
            'Your tax information has been saved.'
          );
          this.isSaving.next(false);
        },
        (err) => {
          // Never log the request body — it carries the TIN. The status and the
          // API's own message are safe and are what make a failure diagnosable.
          console.log('HTTP Error', err?.status, err?.error?.message);
          this.toast.showToast('Error!', this.saveErrorMessage(err), 'danger');
          this.isSaving.next(false);
        }
      );
  }

  /**
   * Prefers the API's message over a generic one. A 400 also carries per-field
   * errors, which name the field the server rejected — worth showing, because
   * the client and server validation rules can drift.
   */
  private saveErrorMessage(err: any): string {
    if (err?.status === 0) {
      return 'Could not reach the server. Check your connection and try again.';
    }

    const message = err?.error?.message;
    const fields = err?.error?.errors;

    if (err?.status === 400 && fields) {
      const first = Object.keys(fields)[0];
      return first ? `${fields[first]} (${first})` : message;
    }

    return message ?? 'Something went wrong!';
  }

  /** Amounts are calculated from the wallet ledger, never user input. */
  public getReportedAmounts(tax_year: number) {
    this.isLoading_amounts.next(true);

    this.whenAuthenticated()
      .pipe(
        switchMap(() =>
          this.api.get(`tax_form?type=get_reported_amounts&tax_year=${tax_year}`)
        ),
        take(1)
      )
      .subscribe(
        (res: irs_reported_amounts) => {
          this.amounts.next(res);
          this.isLoading_amounts.next(false);
        },
        (err) => {
          this.amounts.next(null);
          this.isLoading_amounts.next(false);
        }
      );
  }

  /**
   * Everything needed to render the statement: the recipient block, the full
   * taxpayer ID, and Box 1a for the year.
   *
   * Returned to the caller rather than pushed into a subject, deliberately.
   * This is the only response in the app that carries an unmasked TIN, so it
   * must not outlive the form being on screen -- a BehaviorSubject would hold
   * it for the lifetime of the session. The caller drops it on close.
   *
   * Fetched fresh every time for the same reason the amounts are: a downloaded
   * PDF is kept and forwarded, so it must not print a figure the user last
   * loaded ten minutes and three payments ago.
   */
  public fetchStatement(tax_year: number): Observable<irs_statement> {
    return this.api
      .get(`tax_form?type=get_statement&tax_year=${tax_year}`)
      .pipe(take(1)) as Observable<irs_statement>;
  }
}
